import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  CloseRoundedIcon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Form,
  FormTextField,
  IconButton,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useRichTextEditor } from '../../providers';
import { TableIcon } from '../icons';

import ControlButton from './control-button';

const TableCreationFormSchema = z.object({
  rows: z.coerce.number().min(1, { message: 'Please specify number of row(s)' }),
  columns: z.coerce.number().min(1, { message: 'Please specify number of column(s)' }),
});

type TableCreationFormSchema = z.infer<typeof TableCreationFormSchema>;

export default function RichTextEditorTableControl() {
  const { editor } = useRichTextEditor();
  const formContext = useForm<TableCreationFormSchema>({
    defaultValues: {
      rows: 3,
      columns: 3,
    },
    resolver: zodResolver(TableCreationFormSchema),
  });
  const [isTableCreationModalOpen, tableCreationModalHandlers] = useDisclosure(false);
  const { open: openTableCreationModal, close: closeTableCreationModal } =
    tableCreationModalHandlers;

  const onSubmit = (formData: TableCreationFormSchema) => {
    const { rows, columns } = formData;
    if (editor) {
      editor.chain().focus().insertTable({ rows, cols: columns, withHeaderRow: false }).run();
    }
    closeTableCreationModal();
  };

  return (
    <>
      <ControlButton
        aria-label='Insert Table'
        data-rich-text-editor-control
        onClick={openTableCreationModal}
      >
        <TableIcon />
        <Box className='sr-only' component='span' sx={{ display: 'none' }}>
          Insert Table
        </Box>
      </ControlButton>
      <Dialog open={isTableCreationModalOpen} onClose={closeTableCreationModal} fullWidth>
        <FormProvider {...formContext}>
          <Form
            onSubmit={(event) => {
              event.stopPropagation();
              formContext.handleSubmit(onSubmit)(event);
            }}
          >
            <DialogTitle>Insert Table</DialogTitle>
            <IconButton
              aria-label='close'
              onClick={closeTableCreationModal}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
            <DialogContent>
              <Flex sx={{ gap: (theme) => theme.spacing(4), flexDirection: 'row' }}>
                <FormTextField name='rows' label='Rows' />
                <FormTextField name='columns' label='Columns' />
              </Flex>
            </DialogContent>
            <DialogActions>
              <Button type='submit'>Insert</Button>
            </DialogActions>
          </Form>
        </FormProvider>
      </Dialog>
    </>
  );
}
